import { useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  Column,
  Row,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MoreHorizontal,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  User,
  Building,
  DollarSign,
  Activity,
  Edit,
  Trash,
} from "lucide-react";
import { useState, useMemo } from "react";
import { apiService } from "@/lib/api-service";
import { useAuth } from "@/lib/auth-context";
import { UserRole } from "@employee-management/common";
import { filterData } from "@/lib/filter-utils";
import type { Employee } from "@/types/employee.types";
import { formatCurrency } from "@employee-management/common";
import { FilterSettings } from "@/components/ui/search-filter-bar";

interface EmployeeTableProps {
  filterSettings?: FilterSettings;
  onEdit?: (employee: Employee) => void;
  onDelete?: (employee: Employee) => void;
}

export function EmployeeTable({
  filterSettings,
  onEdit,
  onDelete,
}: EmployeeTableProps) {
  const { hasRole } = useAuth();
  const {
    data: rawData = [],
    isLoading,
    isError,
  } = useQuery<Employee[]>({
    queryKey: ["employees"],
    queryFn: () => apiService.get<Employee[]>("/employees"),
  });

  // Filter data based on filterSettings
  const data = useMemo(() => {
    if (!filterSettings) return rawData;
    return filterData(rawData, filterSettings, [
      "name",
      "email",
      "internalEmployeeId",
    ]);
  }, [rawData, filterSettings]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);

  const columns: ColumnDef<Employee>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <div className="w-[30ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <User className="h-4 w-4" />
            Name
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="font-medium flex items-center gap-3">
          <div className="flex items-center justify-center p-3 bg-muted rounded-lg">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <p>{row.getValue("name")}</p>
            <p className="text-xs text-muted-foreground">
              {row.original.email}
            </p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "internalEmployeeId",
      header: ({ column }) => (
        <div className="w-[12ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <User className="h-4 w-4" />
            Internal ID
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const internalId = row.getValue("internalEmployeeId") as string;
        return internalId || <span className="text-muted-foreground">-</span>;
      },
    },
    {
      accessorKey: "department",
      header: ({ column }) => (
        <div className="w-[15ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Building className="h-4 w-4" />
            Department
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
    },
    // Only show hourly rate column for managers and superadmins
    ...(hasRole(UserRole.MANAGER)
      ? [
          {
            accessorKey: "hourlyRate",
            header: ({ column }: { column: Column<Employee, unknown> }) => (
              <div className="w-[12ch]">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                  className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
                >
                  <DollarSign className="h-4 w-4" />
                  Hourly Rate
                  {column.getIsSorted() === "asc" ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : column.getIsSorted() === "desc" ? (
                    <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ),
            cell: ({ row }: { row: Row<Employee> }) => {
              const hourlyRate = row.getValue("hourlyRate") as number;
              return formatCurrency(hourlyRate);
            },
          },
        ]
      : []),
    {
      accessorKey: "isActive",
      header: ({ column }) => (
        <div className="w-[12ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Activity className="h-4 w-4" />
            Status
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <Badge variant={row.getValue("isActive") ? "default" : "secondary"}>
          {row.getValue("isActive") ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    // Only show actions column for managers and superadmins
    ...(hasRole(UserRole.MANAGER)
      ? [
          {
            id: "actions",
            header: " ",
            cell: ({ row }: { row: Row<Employee> }) => {
              const employee = row.original;
              return (
                <div className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit?.(employee)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onDelete?.(employee)}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Trash className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) return <div>Loading...</div>;
  if (isError)
    return <div className="text-red-500">Failed to load employees.</div>;

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="!bg-muted/40">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className="hover:bg-transparent"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No employees found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
